import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { BuildingOfficeIcon, CalendarIcon, MapPinIcon } from '@heroicons/react/24/solid';

const DetailItem = ({ label, value, icon }) => (
    <div className="flex items-start">
        <div className="flex-shrink-0 mt-1">
            {icon}
        </div>
        <div className="ml-3">
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</h4>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{value || 'Not provided'}</p>
        </div>
    </div>
);

export default function OrganizationProfilePage() {
  const { user, token } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const location = useLocation();
  const isSidebarOpen = location?.state?.isSidebarOpen;

  useEffect(() => {
    const fetchProfile = async () => {
      if (!token) {
        setError("Authentication token not found.");
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/organization`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) {
          throw new Error('Failed to fetch organization profile data.');
        }
        const data = await response.json();
        setProfileData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [token]);

  if (loading) {
    return <div className="text-center p-10">Loading organization profile...</div>;
  }

  if (error) {
    return <div className="text-center p-10 text-red-500">{error}</div>;
  }

  const orgName = profileData?.organization_name || user?.organizationName;
  const displayEmail = profileData?.email || user?.email;
  const profileImageUrl = profileData?.profile_photo_url || `https://ui-avatars.com/api/?name=${orgName}&background=e46809&color=fff`;

  return (
    <div className={`max-w-4xl mx-auto ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
      <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">Organization Profile</h1>
      <p className="mt-1 text-gray-500 dark:text-gray-400">Your organization's information and registration details.</p>

      <div className="mt-8 bg-white dark:bg-gray-800 shadow-lg rounded-xl overflow-hidden">
        <div className="p-8 flex flex-col sm:flex-row items-center gap-8 border-b border-gray-200 dark:border-gray-700">
          <img 
            src={profileImageUrl}
            alt="Organization Logo"
            className="w-32 h-32 rounded-full object-cover border-4 border-orange-500" 
          />
          <div className="text-center sm:text-left">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">{orgName}</h2>
            <p className="text-md text-gray-500 dark:text-gray-400">{displayEmail}</p>
          </div>
        </div>

        <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">Organization Details</h3>
            <div className="space-y-6">
              <DetailItem 
                label="Organization Name" 
                value={profileData?.organization_name} 
                icon={<BuildingOfficeIcon className="w-5 h-5 text-gray-400" />}
              />
              <DetailItem 
                label="Year Established" 
                value={profileData?.year_of_establishment} 
                icon={<CalendarIcon className="w-5 h-5 text-gray-400" />}
              />
              <DetailItem 
                label="State of Operation" 
                value={profileData?.state_of_operation} 
                icon={<MapPinIcon className="w-5 h-5 text-gray-400" />}
              />
            </div>
          </div>
          
          <div>
            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">Registration Documents</h3>
            <div className="space-y-6">
              <DetailItem 
                label="Document Type" 
                value={profileData?.id_type} 
                icon={<BuildingOfficeIcon className="w-5 h-5 text-gray-400" />}
              />
              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Registration Document</h4>
                {profileData?.id_document_url ? (
                  <a 
                    href={profileData.id_document_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-block text-lg font-semibold text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 underline"
                  >
                    View Document
                  </a>
                ) : (
                  <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">Not provided</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {profileData?.organization_bio && (
          <div className="p-8 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">Organization Mission</h3>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              {profileData.organization_bio}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}